import { BreakPoint } from '@/hooks/useBreakPoint/consts';
import useDataLayer from '@/hooks/useDataLayer/useDataLayer';
import {
  MAIN_HEADER_SEARCH_COLLAPSIBLE,
  PREVENT_DISMISS_PROP,
} from '@/hooks/useHeader/consts';
import useIsMobile from '@/hooks/useIsMobile/useIsMobile';
import useNavigate from '@/hooks/useNavigate/useNavigate';
import { useSafeLayoutEffect } from '@/hooks/useSafeLayoutEffect/useSafeLayoutEffect';
import Clickable from '@uikit/components/Clickable/Clickable';
import { ClickableAction } from '@uikit/components/Clickable/consts';
import Icon from '@uikit/components/Icon/Icon';
import LockBodyScroll from '@uikit/components/LockBodyScroll/LockBodyScroll';
import clsx from 'clsx';
import { CSSProperties, KeyboardEvent, useRef } from 'react';
import MainHeader from '../MainHeader/MainHeader';
import styles from './SearchAddon.module.scss';
import { SearchAddonProps } from './types';

const SearchAddon = ({
  isCollapsibleActive,
  isHidden = false,
  isInverted = false,
  onCollapsibleToggle,
  quickLinks = [],
  suggestionsBoxMobileOffset = 0,
  suggestionsBoxOffset = 0,
}: SearchAddonProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile(BreakPoint.LG);
  const { pushDataLayerEvent } = useDataLayer();

  const handleQueryKeyUp = (event: KeyboardEvent<HTMLInputElement>) => {
    const query = (event.target as HTMLInputElement).value;

    if (event.key === 'Enter') {
      pushDataLayerEvent({
        dataLayerVersion: '2.0',
        event: 'internalSearch',
        filterUsed: 'nein',
        functionDetails: 'Standard',
        internalSearchFilterConfig: 'none',
        internalSearchTerm: query === '' ? '(none)' : query.toLowerCase(),
      });

      navigateToResults(query);
    }
  };

  const navigate = useNavigate();
  const navigateToResults = (query: string) => {
    navigate(`/suche?s=${query}&f=global`);
  };

  const toggleSearch = () =>
    onCollapsibleToggle(MAIN_HEADER_SEARCH_COLLAPSIBLE);

  const showSuggestions = isMobile || (quickLinks?.length || 0) > 0;

  const suggestionsBoxStyle = {
    '--mobile-offset': `${suggestionsBoxMobileOffset}px`,
    '--offset': `${suggestionsBoxOffset}px`,
  } as CSSProperties;

  useSafeLayoutEffect(() => {
    if (isCollapsibleActive) {
      inputRef.current?.focus();
    }
  }, [isCollapsibleActive]);

  return (
    <>
      {isCollapsibleActive && <LockBodyScroll />}

      <MainHeader.Addon
        aria-controls="main-header-search-bar"
        aria-hidden={isHidden}
        icon="action/search"
        isHidden={isHidden}
        isInverted={isInverted}
        onClick={toggleSearch}
        title="Suche"
        {...PREVENT_DISMISS_PROP}
      />

      <div
        aria-hidden={!isCollapsibleActive}
        className={clsx(styles.base, {
          [styles.isActive]: isCollapsibleActive,
          [styles.isInverted]: isInverted,
        })}
        id="main-header-search-bar"
      >
        <div aria-hidden="true" className={styles.flexShifter} />

        <div className={styles.searchBar} {...PREVENT_DISMISS_PROP}>
          <Icon isOutlined size="iconSize20" variant="action/basic-search" />
          <input
            autoFocus={isCollapsibleActive}
            className={styles.searchInput}
            onKeyUp={handleQueryKeyUp}
            placeholder="Suche"
            ref={inputRef}
            tabIndex={isCollapsibleActive ? 0 : -1}
            type="search"
          />
        </div>

        <button
          aria-controls="main-header-search-bar"
          aria-label="Suche schließen"
          className={styles.button}
          onClick={toggleSearch}
          tabIndex={isCollapsibleActive ? 0 : -1}
          {...PREVENT_DISMISS_PROP}
        >
          <Icon
            aria-hidden="true"
            size={isMobile ? 'iconSize20' : 'iconSize24'}
            variant="action/close"
          />
        </button>
      </div>

      {showSuggestions && (
        <div
          className={clsx(styles.suggestionsBox, {
            [styles.isActive]: isCollapsibleActive,
          })}
          style={suggestionsBoxStyle}
          {...PREVENT_DISMISS_PROP}
          aria-label="Suchvorschläge"
        >
          <div className={styles.suggestionsTitle}>Quick Links</div>
          <ul className={styles.suggestionsList}>
            {quickLinks?.map((quickLink) => (
              <li key={quickLink.displayText}>
                <Clickable
                  actionType={ClickableAction.Linkextern}
                  className={styles.suggestion}
                  clickContext="Interne Suche (Website) Quick-Tipp"
                  linkextern={quickLink.url}
                  tabIndex={isCollapsibleActive ? 0 : -1}
                  customDataLayer={{
                    dataLayerVersion: '2.0',
                    clickContext: 'Interne Suche Quicklink',
                    customClickText: quickLink.displayText,
                    functionDetails: 'Standard',
                  }}
                >
                  {quickLink.displayText}
                </Clickable>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
};

export default SearchAddon;
